import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Button, Modal, Select } from 'antd';
import { useMutation } from '@apollo/client';
import classnames from 'classnames';
import gql from 'graphql-tag';

import formatPrice from '~helpers/currency';

const REJECT_ORDER_REFUND = gql`
  mutation rejectOrderRefundByLandlord($input: RejectOrderRefundByFinancialInput!) {
    rejectOrderRefundByLandlord(input: $input) {
      result
    }
  }
`;

const RejectModal = ({ t, visible, record = {}, modalType, onCancel, onSuccess }) => {
  const [showErrorTip, setShowErrorTip] = useState(false);
  const [rejectReason, setRejectReason] = useState(undefined);

  const handleCloseOrOpenModal = () => {
    setShowErrorTip(false);
    setRejectReason(undefined);

    onCancel?.();
  };

  const [rejectOrderRefund, { loading }] = useMutation(REJECT_ORDER_REFUND, {
    variables: {
      input: {
        id: record?.id,
        rejectReason,
      },
    },
    onCompleted: () => {
      handleCloseOrOpenModal();

      onSuccess?.();
    },
  });

  const handleReject = () => {
    if (!rejectReason) {
      setShowErrorTip(true);
    } else {
      rejectOrderRefund();
    }
  };

  return (
    <div className="reject-modal">
      <Modal
        visible={visible}
        width={880}
        className="reject-modal__content"
        title={t('cms:cms.billing.refund.table.reject_modal.title')}
        footer={[
          <Button key="back" style={{ height: 40 }} onClick={handleCloseOrOpenModal}>
            {t('cms:cms.billing.transfer.cancel.btn')}
          </Button>,
          <Button
            key="submit"
            type="danger"
            style={{ height: 40, marginLeft: 16 }}
            loading={loading}
            onClick={handleReject}
          >
            {t('cms:cms.properties.pending_approval.review_modal.reject.btn')}
          </Button>,
        ]}
        onCancel={handleCloseOrOpenModal}
      >
        <div className="reject-modal__basic-info">
          <div className="reject-modal__basic-info-desc">
            <p className="reject-modal__basic-info-text">
              {t('cms:cms.billing.refund.table.reject_modal.notice')}
            </p>
            <p className="reject-modal__basic-info-text">
              {t('cms:cms.billing.refund.table.reject_modal.notice1')}
            </p>
          </div>
          <div className="reject-modal__invoice-info-form">
            <Row gutter={24}>
              <Col span={12}>
                <span className="reject-modal__label">
                  {t('cms:cms.billing.refund.table.filter.refund_from.label')}:&nbsp;
                  {record?.refundFrom
                    ? t(
                        `cms:cms.billing.refund.table.refund_from.${
                          modalType === 'refund' ? 'landlord' : record?.refundFrom.toLowerCase()
                        }`,
                      )
                    : '-'}
                </span>
                <span className="reject-modal__label">
                  {t('cms:cms.billing.refund.table.refund_method.field')}:&nbsp;
                  {record?.refundMethod
                    ? t(
                        `cms:cms.billing.refund.table.refund_method.${record?.refundMethod.toLowerCase()}`,
                      )
                    : '-'}
                </span>
              </Col>
              <Col span={12}>
                <span className="reject-modal__label">
                  {t('cms:cms.billing.refund.table.filter.refund_to.label')}:&nbsp;
                  {record?.refundTo
                    ? t(`cms:cms.billing.refund.table.refund_to.${record?.refundTo.toLowerCase()}`)
                    : '-'}
                </span>
                <span className="reject-modal__label">
                  {t('cms:cms.billing.refund.table.refund_amount.field')}:&nbsp;
                  {record?.amount ? formatPrice(record?.amount, record?.currency, 2) : '-'}
                </span>
              </Col>
              <Col span={12}>
                <span className="reject-modal__label">
                  {t('cms:cms.billing.refund.table.filter.trade_number.label')}:&nbsp;
                  {record?.transactionNo ?? '-'}
                </span>
              </Col>
            </Row>
          </div>

          <Row className="reject-modal__reason">
            <p className="reject-modal__reason-label">
              {t('cms:cms.billing.refund.table.reject_modal.reject_reason.label')}
            </p>

            <Select
              style={{ width: '100%' }}
              className={classnames(showErrorTip && 'reject-modal__reason-select')}
              value={rejectReason}
              onChange={val => {
                setShowErrorTip(false);
                setRejectReason(val);
              }}
              placeholder={t('cms:cms.billing.refund.table.reject_modal.reject_reason.placeholder')}
            >
              <Select.Option
                value={t('cms:cms.billing.refund.table.reject_modal.reject_reason.first')}
              >
                {t('cms:cms.billing.refund.table.reject_modal.reject_reason.first')}
              </Select.Option>
              <Select.Option
                value={t('cms:cms.billing.refund.table.reject_modal.reject_reason.second')}
              >
                {t('cms:cms.billing.refund.table.reject_modal.reject_reason.second')}
              </Select.Option>
            </Select>
            <If condition={showErrorTip}>
              <p className="reject-modal__reason-error-tip">
                {t('cms:cms.billing.refund.table.reject_modal.reject_reason.error.tip')}
              </p>
            </If>
          </Row>
        </div>
      </Modal>
    </div>
  );
};

RejectModal.propTypes = {
  t: PropTypes.func.isRequired,
  record: PropTypes.object,
  type: PropTypes.string,
};

RejectModal.defaultProps = {
  record: {},
  type: '',
};

export default RejectModal;
